import { useMemo } from 'react';
import { useData } from '../context/DataContext';
import { createDatabaseService } from '../utils/database/DatabaseService';

/**
 * Hook that provides access to the new database service
 * This is a new hook that components can opt into when they want to use the improved architecture
 */
export const useDatabase = () => {
  const { supabase } = useData();

  const dbService = useMemo(() => {
    return createDatabaseService(supabase);
  }, [supabase]);

  return dbService;
};

/**
 * Hook for user operations
 * Provides a clean API for user-related database operations
 */
export const useUserOperations = () => {
  const db = useDatabase();
  return useMemo(() => db.user, [db]);
};

/**
 * Hook for class operations
 * Provides a clean API for class-related database operations
 */
export const useClassOperations = () => {
  const db = useDatabase();
  return useMemo(() => db.class, [db]);
};

/**
 * Hook for student operations
 * Provides a clean API for student-related database operations
 */
export const useStudentOperations = () => {
  const db = useDatabase();
  return useMemo(() => db.student, [db]);
};

/**
 * Hook for homework operations
 * Provides a clean API for homework-related database operations
 */
export const useHomeworkOperations = () => {
  const db = useDatabase();
  return useMemo(() => db.homework, [db]);
};

/**
 * Hook for semester operations
 * Provides a clean API for semester-related database operations
 */
export const useSemesterOperations = () => {
  const db = useDatabase();
  return useMemo(() => db.semester, [db]);
};

/**
 * Hook for cache operations
 * Provides a clean API for cache-related database operations
 */
export const useCacheOperations = () => {
  const db = useDatabase();
  return useMemo(() => db.cache, [db]);
};

/**
 * Hook for storage operations
 * Provides a clean API for storage-related database operations
 */
export const useStorageOperations = () => {
  const db = useDatabase();
  return useMemo(() => db.storage, [db]);
};
